//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { SamplingCampaignRequest } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { SamplingDeliveryMethod } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { CampaignRequestCurrencyField } from '@campaigns/components/campaigns-overview/CampaignRequestCurrencyField';
import { CampaignRequestLocaleField } from '@campaigns/components/campaigns-overview/create-campaign-modal/CampaignRequestLocaleField';
import { CampaignRequestRadioField } from '@campaigns/components/shared/CampaignRequestRadioField';
import { getCouponCodeProviderOptions } from '@campaigns/constants/sampling/SamplingCreateCampaignModalConstants';
import {
  BodyBoldText,
  BodyText,
  CaptionText,
} from '@components/atoms/text/Text';
import { getClientName } from '@redux/selectors/getSessionInfo';

interface PrepaidLocationModalProps {
  deliveryMethod?: SamplingDeliveryMethod;
}

export const PrepaidLocationModal = ({
  deliveryMethod,
}: PrepaidLocationModalProps) => {
  const { getValues, setValue, watch } =
    useFormContext<SamplingCampaignRequest>();
  const clientName = useSelector(getClientName);

  const purchaseOptions = watch('purchaseOption');
  const selectedPurchaseOption = purchaseOptions?.[0];
  const fulfillmentOptionsList = selectedPurchaseOption?.fulfillmentOptions;

  const getAvailableCountryCodesFromFulfillment = () => {
    return Array.from(
      new Set(
        fulfillmentOptionsList?.flatMap(
          (option) => option.availableCountryCodes ?? [],
        ) ?? [],
      ),
    );
  };

  const campaignTypeText = deliveryMethod;
  const isDigitalDelivery =
    deliveryMethod === SamplingDeliveryMethod.DigitalDelivery;

  const handleCodeSupplierChange = (value: string) => {
    const currentValues = getValues();

    if (value === 'client') {
      setValue('fulfillmentOptions', {
        ...currentValues.fulfillmentOptions,
        clientCodes: [],
        clientCodesSupplied: false,
        codeScope: undefined,
        // If there are multiple retailers, keep only the first one since
        // we don't support multiple retailers for client-provided codes
        retailers:
          currentValues.fulfillmentOptions?.retailers?.slice(0, 1) ?? [],
      });
    } else {
      setValue('fulfillmentOptions', {
        ...currentValues.fulfillmentOptions,
        clientCodes: undefined,
        clientCodesSupplied: undefined,
        codeScope: undefined,
      });
    }
  };

  return (
    <Container>
      <BodyBoldText>
        Complete details for your {campaignTypeText} campaign:
      </BodyBoldText>
      <FieldsContainer>
        <FieldSection>
          <BodyText>Country and Language</BodyText>
          <CaptionText>
            Location of participants and content language
          </CaptionText>
          <DropdownSection>
            <CampaignRequestLocaleField
              availableCountryCodes={getAvailableCountryCodesFromFulfillment()}
              shouldHideLabel={true}
            />
          </DropdownSection>
        </FieldSection>
        <FieldSection>
          <CampaignRequestCurrencyField />
        </FieldSection>
        {isDigitalDelivery && (
          <CampaignRequestRadioField
            label='Code Provider'
            labelHelpText='Select whether you want Bazaarvoice to manage the coupon codes in the campaign, or to manage it yourself.'
            name='codeSupplier'
            onChange={handleCodeSupplierChange}
            options={getCouponCodeProviderOptions(clientName)}
          />
        )}
      </FieldsContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldSection = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  width: ${theme.mediumMeasure};
  margin-top: ${theme.spacings[3]};
`,
);

const DropdownSection = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  width: ${theme.smallMeasure};
`,
);
